//  RA-2160 caveat: the IP reported to us comes from a third-party lookup (ipify) made in the
//  browser. It is client-supplied, so it is for audit only and must never drive access control.
//  It is accurate only while the customer's proxy (e.g. Zscaler) leaves that lookup unproxied; if
//  that policy changes, it silently becomes the proxy IP.
const IPIFY_URL = 'https://api.ipify.org?format=json';

/** How long a resolved IP is reused before ipify is asked again. */
export const CLIENT_IP_TTL_MS = 10 * 60 * 1000;

/**
 * How long a failed lookup is remembered. Shorter than the success TTL so a transient blip
 * recovers quickly, but long enough that a network which blocks ipify outright (a proxy such as
 * Zscaler may) doesn't make every API call wait out the timeout below.
 */
export const CLIENT_IP_FAILURE_TTL_MS = 60 * 1000;

/** Callers sit in front of our own API requests, so a slow ipify must not hold them up for long. */
export const CLIENT_IP_TIMEOUT_MS = 2000;

let cached: { ip: string; expiresAt: number } | null = null;
let inFlight: Promise<string> | null = null;

const lookup = async (): Promise<string> => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), CLIENT_IP_TIMEOUT_MS);
  try {
    //  Plain fetch, not the interceptor: the axios instance attaches the JWT, facility and tenant
    //  headers, none of which should be sent to a third party.
    const response = await fetch(IPIFY_URL, { signal: controller.signal });
    if (!response.ok) return '';
    const body = await response.json();
    return typeof body?.ip === 'string' && body.ip ? body.ip : '';
  } catch {
    return '';
  } finally {
    clearTimeout(timer);
  }
};

/**
 * The user's public IP as ipify sees it, for forwarding to our own APIs in a header. Needed because
 * some clients sit behind Zscaler, which strips X-Forwarded-For before the request reaches us.
 *
 * Cached in memory for {@link CLIENT_IP_TTL_MS}; concurrent callers share one in-flight request.
 * Resolves an empty string rather than rejecting when the IP can't be determined, so the caller
 * can send its request without the header instead of failing it.
 */
export const getClientIp = async (): Promise<string> => {
  if (cached && Date.now() < cached.expiresAt) return cached.ip;
  if (inFlight) return inFlight;

  inFlight = lookup()
    .then((ip) => {
      cached = {
        ip,
        expiresAt: Date.now() + (ip ? CLIENT_IP_TTL_MS : CLIENT_IP_FAILURE_TTL_MS),
      };
      return ip;
    })
    .finally(() => {
      inFlight = null;
    });

  return inFlight;
};

/** Test-only: drops the cached IP and any pending lookup. */
export const resetClientIpCache = () => {
  cached = null;
  inFlight = null;
};

/** Header our backend reads the client IP from — X-Forwarded-For doesn't survive Zscaler. */
export const CLIENT_IP_HEADER = 'X-Client-IP';

/**
 * The client-IP header, ready to spread into a request's headers. Empty when the IP is unknown, so
 * the request goes out without the header rather than with a blank one.
 */
export const getClientIpHeader = async (): Promise<Record<string, string>> => {
  const ip = await getClientIp();
  return ip ? { [CLIENT_IP_HEADER]: ip } : {};
};

/**
 * Synchronous variant for requests that cannot await, such as the `keepalive` autosave fired on
 * page unload. Reads the cache only and never starts a lookup.
 */
export const peekClientIpHeader = (): Record<string, string> =>
  cached?.ip && Date.now() < cached.expiresAt ? { [CLIENT_IP_HEADER]: cached.ip } : {};
